/**
 * Sidebar hook (HUB-103): one section per enabled extension (admins only),
 * with the `nav` each one's admin module declares. Settings rows show as soon
 * as the list lands; custom rows appear when the (memoized, shared) chunk
 * resolves.
 */
import { useQuery } from '@tanstack/react-query';
import type { NavSection } from '@/components/Sidebar';
import { isAdminRoles } from '@/lib/require-admin';
import { buildExtensionNavSections } from './extension-nav';
import { loadExtensionAdmin } from './registry';
import { useEnabledExtensions } from './use-extensions';

export function useExtensionNavSections(roles: unknown): NavSection[] {
  const admin = isAdminRoles(roles);
  const enabled = useEnabledExtensions(admin);
  const names = (enabled.data ?? []).map((ext) => ext.name);
  const navs = useQuery({
    queryKey: ['extension-admin-nav', names],
    queryFn: () => Promise.all(names.map((name) => loadExtensionAdmin(name) ?? Promise.resolve(undefined))),
    enabled: names.length > 0,
    staleTime: Infinity,
  });
  if (!admin || !enabled.data) return [];
  return buildExtensionNavSections(
    enabled.data.map((ext, i) => ({ ...ext, nav: navs.data?.[i]?.nav })),
  );
}
